import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

import { usePermissions } from "@/app/session/permissions";
import { CREATE_ACTIONS, createHref } from "@/app/shell/createActions";
import { GO_KEYS } from "@/app/shell/nav";

const SEQUENCE_TIMEOUT_MS = 900;

/** Whether a key press belongs to whatever is being typed into. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.closest("[role='dialog']") !== null
  );
}

interface ShortcutHandlers {
  onPalette: () => void;
  onHelp: () => void;
}

/**
 * App-wide keys: ⌘K palette, ? help, / focus the page's search, c create on this page,
 * g then h/c/l/p/u/r to jump. Never while typing or inside a dialog.
 */
export function useShortcuts({ onPalette, onHelp }: ShortcutHandlers) {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissions();

  useEffect(() => {
    let pendingG = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onPalette();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;

      const key = event.key.toLowerCase();
      if (pendingG && Date.now() - pendingG < SEQUENCE_TIMEOUT_MS) {
        pendingG = 0;
        const to = GO_KEYS[key];
        if (to) {
          event.preventDefault();
          void navigate(to);
        }
        return;
      }
      if (key === "g") {
        pendingG = Date.now();
      } else if (event.key === "?") {
        event.preventDefault();
        onHelp();
      } else if (key === "/") {
        const search = document.querySelector<HTMLInputElement>("[data-page-search]");
        if (search) {
          event.preventDefault();
          search.focus();
        }
      } else if (key === "c") {
        const action = CREATE_ACTIONS.find(
          (candidate) =>
            location.pathname === candidate.path && permissions.has(candidate.permission),
        );
        if (action) {
          event.preventDefault();
          void navigate(createHref(action));
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [navigate, location.pathname, permissions, onPalette, onHelp]);
}
